// Type-aware default width floors/caps for NORMAL record columns (the record's
// own fields: name, location, timestamps, user references, id), mirroring the
// per-attribute-column widths in AttributeValueSchema.getAttributeColumnCellStyle.
//
// WHY: with native table auto-layout a column's width is driven by the intrinsic
// content width of the first rendered rows, so early columns with long content
// (notably the leading Name column) hog the available width and later columns
// (attribute/value columns) get squeezed to nothing. Giving each record column a
// readable min-width floor and a max-width cap redistributes the space and keeps
// the layout understandable regardless of row content. The cap ellipsizes long
// cells (RecordLabel is nowrap + text-overflow: ellipsis) rather than overflowing.
//
// Keyed by the legacy column `key`. Returns a `cellStyle` in the shape RecordTable
// threads onto BOTH the body and header cell (`{ style: { minWidth, maxWidth } }`),
// so the width constraint holds symmetrically (a long header label can't force a
// column past its cap). Unknown keys return `undefined` (no constraint) so only
// the record columns we intend to size are affected.
const RECORD_COLUMN_WIDTH: Record<string, { minWidth: number; maxWidth: number }> = {
    // The leading record column — the usual "first columns hog the width" culprit.
    // Generous floor (badges + name), bounded cap so it stops starving later columns.
    name: { minWidth: 180, maxWidth: 320 },
    // Location reference (project/bundle/workspace label) — a short-ish reference.
    location: { minWidth: 120, maxWidth: 220 },
    // Timestamps render as a relative "… ago" label — narrow and fixed-ish.
    created_at: { minWidth: 110, maxWidth: 170 },
    modified_at: { minWidth: 110, maxWidth: 170 },
    // User references (identity label + icon) — bounded like other name-ish cells.
    created_by: { minWidth: 120, maxWidth: 200 },
    modified_by: { minWidth: 120, maxWidth: 200 },
    // UUID id column (monospace, hidden by default) — bounded so it can't hog width.
    id: { minWidth: 130, maxWidth: 280 },
    // Free-text / markdown description — clipped in the cell renderer; matches the
    // large-content attribute-column width (form-markdown) so an added Description
    // column reads consistently and its long content can't hog the row.
    description: { minWidth: 260, maxWidth: 480 },
    // record_log.subject — the main free-text column of the execution logs table.
    // Bounded so the subject renders on a SINGLE line and ellipsizes to the column
    // width (via the OverflowTooltip cell renderer) instead of wrapping to a second
    // line, and so it can't push the whole table into a page-level horizontal scroll.
    subject: { minWidth: 240, maxWidth: 480 },
};

/**
 * Resolve a `cellStyle` (`{ style: { minWidth, maxWidth } }`) for a normal record
 * column by its legacy column `key`. Returns `undefined` for keys with no configured
 * width so unrelated columns keep their default auto-layout behaviour.
 */
export const getRecordColumnCellStyle = (
    key: string
): { style: { minWidth: number; maxWidth: number } } | undefined => {
    const width = RECORD_COLUMN_WIDTH[key];
    if (!width) {
        return undefined;
    }
    return { style: { minWidth: width.minWidth, maxWidth: width.maxWidth } };
};
